import { ListBulletIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SettingKey } from 'config'
import { useEffect, useState } from 'react'
import { getAdminConfig, setAdminConfig } from 'services'
import { ButtonGroup } from 'stories/components'
import { Tooltip } from 'stories/components/Tooltip/Tooltip'
import { ConvertInputValue, InputConvert, InputValidate, isEmpty } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constants'
import { GlobalSettingsV2 } from './GlobalSettingsV2'
import { ILoanGlobalSettings, IMinMaxLimitPerTypes } from './interface'
import ManualPricingConfig from './ManualPricingConfig'
import { AppraisalStatusOrder, MinMaxLimitPerTypes } from './Modal'
import { NexysConnectionSettings } from './NexysConnectionSettings'

const MinMaxPerTypesKeys = ['commitmentFeeRequiredTypes', 'dueDilligenceRequiredTypes']

const buttonGroups: Record<string, string> = {
  loanStructure: 'Loan Structure',
  views: 'Views',
  pricing: 'Manual Pricing',
  security: 'Security',
  setup: 'Setup',
}
export const GlobalSettings = () => {
  const [action, setAction] = useState('')
  const [inputs, setInputs] = useState(defaultInputs())
  const [modal, setModal] = useState('')
  const [globalSetting, setGlobalSetting] = useState<ILoanGlobalSettings>()
  const [selectedItem, setSelectedItem] = useState<IMinMaxLimitPerTypes[]>([])
  const [selectedKey, setSelectedKey] = useState('')
  const [loanNo, setLoanNo] = useState('')
  const [originLoanNo, setOriginLoanNo] = useState('')
  const [subTab, setSubTab] = useState('loanStructure')

  useEffect(() => {
    setAction('setting')
    getAdminConfig(SettingKey.GLOBAL_SETTINGS)
      .then((content) => {
        setGlobalSetting(content)
        let newInputs = cloneDeep(inputs)
        Object.keys(inputs).map((key) => {
          if (newInputs[key].inputType === 'multiselect') newInputs[key].value = content[key] ?? {}
          else if (!MinMaxPerTypesKeys.includes(key)) newInputs[key].value = content[key] ?? ''
        })
        setInputs(newInputs)
      })
      .finally(() => setAction(''))
    getAdminConfig('loan_no').then((value: any) => {
      setLoanNo(value)
      setOriginLoanNo(value)
    })
  }, [])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const onBlur = async (key: string) => {
    if (MinMaxPerTypesKeys.includes(key)) return
    if (!globalSetting) return
    let hasError = false

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = ConvertInputValue(newInputs[key], newInputs[key].value)
      if (newInputs[key].error) hasError = true
    }
    setInputs(newInputs)
    if (hasError) return

    const newSettings = cloneDeep(globalSetting)
    Object.keys(data).forEach((key) => {
      if (data[key] !== undefined && !MinMaxPerTypesKeys.includes(key)) (newSettings as any)[key] = data[key]
    })

    setAction('setting')
    await setAdminConfig(SettingKey.GLOBAL_SETTINGS, newSettings)
    setAction('')
  }

  const onChangeAppraisalStatusOrder = async (newData: Record<string, boolean>) => {
    if (!globalSetting) return

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      if (key === 'appraisalStatisticsVisible') {
        data[key] = newData
        newInputs[key].value = newData
      }
    }
    setInputs(newInputs)

    const newSettings = cloneDeep(globalSetting)
    newSettings.appraisalStatisticsVisible = newData

    setAction('setting')
    await setAdminConfig(SettingKey.GLOBAL_SETTINGS, newSettings)
    setAction('')
    setModal('')
  }

  const onUpdateMinMaxLimitPerTypes = async (newTypes: any[]) => {
    if (!globalSetting) return

    const newSetting = cloneDeep(globalSetting)
    ;(newSetting as any)[selectedKey] = newTypes

    setAction('setting')
    await setAdminConfig(SettingKey.GLOBAL_SETTINGS, newSetting)
    setGlobalSetting(newSetting)
    setAction('')
    setModal('')
  }

  const onLoanNoChange = async (key: string, value: string) => {
    if (value) {
      value = Math.floor(Number(value)).toString()
    }
    setLoanNo(value)
  }

  const onLoanNoSubmit = async () => {
    if (loanNo == originLoanNo) return
    setAction('setLoanNumber')
    const res = await setAdminConfig('loan_no', { loanNo: loanNo || '0' })
    if (res.success) setOriginLoanNo(loanNo)
    else setLoanNo(originLoanNo)
    setAction('')
  }

  return (
    <div className="relative">
      <LayoutLoading show={!isEmpty(action)} />

      <ButtonGroup title={buttonGroups} value={subTab} onChange={setSubTab} />

      {subTab === 'loanStructure' && (
        <div className="relative mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(inputs).map((key, index) => {
            let input = inputs[key]
            let additionalElements = null

            if (key === 'appraisalStatisticsVisible')
              additionalElements = (
                <Tooltip message="Change Order">
                  <ListBulletIcon
                    className="w-5 h-5 text-shade-blue cursor-pointer"
                    onClick={() => setModal('appraisalStatusOrder')}
                  />
                </Tooltip>
              )
            if (MinMaxPerTypesKeys.includes(key))
              additionalElements = (
                <Tooltip message="Edit">
                  <PencilSquareIcon
                    className="w-4 h-4 text-shade-blue cursor-pointer"
                    onClick={() => {
                      if (key === 'commitmentFeeRequiredTypes')
                        setSelectedItem(globalSetting?.commitmentFeeRequiredTypes || [])
                      else setSelectedItem(globalSetting?.dueDilligenceRequiredTypes || [])
                      setSelectedKey(key)
                      setModal('editModal')
                    }}
                  />
                </Tooltip>
              )

            return (
              <div className={`input md:col-span-${input.span || 1} `} key={index}>
                <RenderInput
                  input={{ ...input, additionalElements }}
                  Key={key}
                  onChange={onChange}
                  onBlur={(key: any) => onBlur(key)}
                />
              </div>
            )
          })}
        </div>
      )}

      {['views', 'security'].includes(subTab) && <GlobalSettingsV2 tab={subTab} />}

      {subTab === 'pricing' && (
        <ManualPricingConfig globalSetting={globalSetting} setGlobalSetting={setGlobalSetting} setAction={setAction} />
      )}
      {subTab === 'setup' && (
        <>
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="input md:col-span-full">
              <RenderInput
                input={{ inputType: 'section', title: 'Company Setup' }}
                Key="loanNoSection"
                onChange={() => {}}
              />
            </div>
            <div className="input md:col-span-1">
              <RenderInput
                input={{ inputType: 'text', title: 'Loan Number starts from', type: 'number', value: loanNo }}
                Key="loanNo"
                onChange={onLoanNoChange}
                onBlur={() => onLoanNoSubmit()}
              />
              <div className="mt-4">
                <GlobalSettingsV2 tab={subTab} />
              </div>
            </div>
          </div>
          <div className="relative mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="input md:col-span-1">
              <NexysConnectionSettings
                globalSetting={globalSetting}
                setGlobalSetting={setGlobalSetting}
                setAction={setAction}
              />
            </div>
          </div>
        </>
      )}
      {modal === 'appraisalStatusOrder' && (
        <AppraisalStatusOrder
          data={inputs.appraisalStatisticsVisible.value}
          isOpen={modal === 'appraisalStatusOrder'}
          onClose={() => setModal('')}
          onSubmit={onChangeAppraisalStatusOrder}
          loading={action}
        />
      )}
      {modal === 'editModal' && !!selectedKey && !!selectedItem && (
        <MinMaxLimitPerTypes
          selectedKey={selectedKey}
          data={selectedItem}
          isOpen={modal === 'editModal' && !!selectedKey && !!selectedItem}
          onClose={() => setModal('')}
          loading={action}
          onSubmit={onUpdateMinMaxLimitPerTypes}
        />
      )}
    </div>
  )
}
